import * as React from 'react';
import {mirana} from '@wize/mirana-proto/mirana-proto';
import {Field, InjectedFormProps, reduxForm, reset} from 'redux-form';
import {Button, ButtonGroup, Col} from 'reactstrap';
import renderSelectField from '../../../theme/components/form/Select';
import renderIntervalDatePickerField from '../../../theme/components/form/IntervalDatePicker';
import Panel from '../../../theme/components/Panel';
import validate from './validate';
import * as moment from 'moment';
import {lazyInject, TYPES} from '../../../utils/inversify-config';
import MiranaServiceClient from '../../../middleware/mirana-client';
import {error} from '../../../utils/notification-actions';

interface RechazoFormState {
    fuerzasVenta: mirana.FuerzaVentaList;
    mesas: mirana.MesaList;
    rutas: mirana.RutaList;
    rutasDespacho: mirana.RutaDespachoList;
    motivosAnulacion: mirana.MotivoAnulacionList;
    fuerzaVentaId: string;
}

class RechazosForm extends React.PureComponent<InjectedFormProps & ReduxProps, RechazoFormState> {

    @lazyInject(TYPES.MIRANA_SERVICE)
    public apiClient: MiranaServiceClient;

    constructor(props) {
        super(props);

        this.state = {
            fuerzasVenta: mirana.FuerzaVentaList.create(),
            mesas: mirana.MesaList.create(),
            rutas: mirana.RutaList.create(),
            rutasDespacho: mirana.RutaDespachoList.create(),
            motivosAnulacion: mirana.MotivoAnulacionList.create(),
            fuerzaVentaId: ''
        };
        this.fetchFuerzasVenta();
        this.fetchMesas();
        this.fetchRutas();
        this.fetchRutasDespacho();
        this.fetchMotivosAnulacion();
    };

    private async fetchFuerzasVenta() {
        try {
            const response = await this.apiClient.obtenerFuerzasVenta();
            this.setState({fuerzasVenta: response});
        } catch (ex) {
            this.props.dispatch(error('Error', 'Error al obtener fuerzas de venta'));
        }
    }

    private async fetchMesas(fuerzaVentaId?: string) {
        try {
            const response = await this.apiClient.filtrarMesas(fuerzaVentaId);
            this.setState({mesas: response});
        } catch (ex) {
            this.props.dispatch(error('Error', 'Error al obtener mesas'));
        }
    }

    private async fetchRutas(fuerzaVentaId?: string, mesaId?: string) {
        try {
            const response = await this.apiClient.filtrarRutas(fuerzaVentaId, mesaId);
            this.setState({rutas: response});
        } catch (ex) {
            this.props.dispatch(error('Error', 'Error al obtener vendedores'));
        }
    }

    private async fetchRutasDespacho() {
        try {
            const response = await this.apiClient.obtenerRutasDespacho();
            this.setState({rutasDespacho: response});
        } catch (ex) {
            this.props.dispatch(error('Error', 'Error al obtener rutas de despacho'));
        }
    }

    private async fetchMotivosAnulacion() {
        try {
            const response = await this.apiClient.obtenerMotivosAnulacion();
            this.setState({motivosAnulacion: response});
        } catch (ex) {
            this.props.dispatch(error('Error', 'Error al obtener motivos de anulación'));
        }
    }

    private get fuerzaVentaOptions() {
        const fuerzaVenta = this.state.fuerzasVenta;
        const options = [
            {value: '', label: 'Todos'}
        ];
        if (fuerzaVenta.fuerzaVenta) {
            fuerzaVenta.fuerzaVenta.forEach((fv) => {
                if (fv.fuerzaVentaId && fv.descripcion) {
                    options.push({value: fv.fuerzaVentaId, label: fv.descripcion});
                }
            });
        }
        return options;
    }

    private get mesaOptions() {
        const mesaList = this.state.mesas;
        const options = [
            {value: '', label: 'Todos'}
        ];
        if (mesaList.mesa) {
            mesaList.mesa.forEach((me) => {
                if (me.mesaId && me.descripcion) {
                    options.push({value: me.mesaId, label: me.descripcion});
                }
            });
        }
        return options;
    }

    private get rutaOptions() {
        const rutaList = this.state.rutas;
        const options = [
            {value: '', label: 'Todos'}
        ];
        if (rutaList.ruta) {
            rutaList.ruta.forEach((rt) => {
                if (rt.rutaId && rt.codigo) {
                    options.push({value: rt.rutaId, label: rt.codigo});
                }
            });
        }
        return options;
    }

    private get motivoAnulacionOptions() {
        const motivosList = this.state.motivosAnulacion;
        const options = [
            {value: '', label: 'Todos'}
        ];
        if (motivosList.motivoAnulacion) {
            motivosList.motivoAnulacion.forEach((ma) => {
                if (ma.motivoAnulacionId && ma.descripcion) {
                    options.push({value: ma.motivoAnulacionId, label: ma.descripcion});
                }
            });
        }
        return options;
    }

    private get rutaDespachoOptions() {
        const rutaDespachoList = this.state.rutasDespacho;
        const options = [
            {value: '', label: 'Todos'}
        ];
        if (rutaDespachoList.rutaDespacho) {
            rutaDespachoList.rutaDespacho.forEach((rd) => {
                if (rd.rutaDespachoId && rd.codigo) {
                    options.push({value: rd.rutaDespachoId, label: rd.codigo});
                }
            });
        }
        return options;
    }

    private handleMesaChange = (obj) => {
        this.fetchRutas(this.state.fuerzaVentaId, obj.value);
    };

    private handleFuerzaVentaChange = (obj) => {
        this.setState({fuerzaVentaId: obj.value})
        this.fetchMesas(obj.value);
        this.fetchRutas(obj.value)
    };

    private handleReset = (a, b) => {
        this.props.dispatch(reset('rechazos_form'));
        this.fetchMesas();
        this.fetchRutas();
    };

    public render() {
        const {handleSubmit, valid} = this.props;

        return (
            <Panel xs={12} md={12} divider={true} title='Filtros'>
                <form className='form' onSubmit={handleSubmit}>
                    <Col md={4} lg={4}>
                        <div className='form__form-group'>
                            <label className='form__form-group-label'>Rango de Fechas</label>
                            <div className='form__form-group-field'>
                                <Field
                                    name='intervalo'
                                    component={renderIntervalDatePickerField}
                                    defaultDate={moment().subtract(1, 'days')}
                                    filterDate={(date) => {
                                        return moment().subtract(1, 'days') > date;
                                    }}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col md={4} lg={4}>
                        <div className='form__form-group'>
                            <label className='form__form-group-label'>Fuerza de Venta </label>
                            <div className='form__form-group-field'>
                                <Field
                                    name='fuerzaVenta'
                                    component={renderSelectField}
                                    onChange={this.handleFuerzaVentaChange}
                                    placeholder='Todos'
                                    type='text'
                                    options={this.fuerzaVentaOptions}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col md={4} lg={4}>
                        <div className='form__form-group'>
                            <label className='form__form-group-label'>Mesa </label>
                            <div className='form__form-group-field'>
                                <Field
                                    name='mesa'
                                    component={renderSelectField}
                                    onChange={this.handleMesaChange}
                                    placeholder='Todos'
                                    type='text'
                                    options={this.mesaOptions}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col md={4} lg={4}>
                        <div className='form__form-group'>
                            <label className='form__form-group-label'>Vendedor </label>
                            <div className='form__form-group-field'>
                                <Field
                                    name='vendedor'
                                    component={renderSelectField}
                                    placeholder='Todos'
                                    type='text'
                                    options={this.rutaOptions}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col md={4} lg={4}>
                        <div className='form__form-group'>
                            <label className='form__form-group-label'>Ruta de Despacho </label>
                            <div className='form__form-group-field'>
                                <Field
                                    name='rutaDespacho'
                                    component={renderSelectField}
                                    placeholder='Todos'
                                    type='text'
                                    options={this.rutaDespachoOptions}
                                />
                            </div>
                        </div>
                    </Col>

                    <Col md={4} lg={4}>
                        <div className='form__form-group'>
                            <label className='form__form-group-label'>Motivo de Anulaci&oacute;on </label>
                            <div className='form__form-group-field'>
                                <Field
                                    name='motivoAnulacion'
                                    component={renderSelectField}
                                    placeholder='Todos'
                                    type='text'
                                    options={this.motivoAnulacionOptions}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col sm={12} md={{size: 4, offset: 8}}>
                        <ButtonGroup className='btn-group--justified'>
                            <Button outline={true} disabled={!valid} type='button'
                                    onClick={this.handleReset}>LIMPIAR</Button>
                            <Button color='success' type='submit'>FILTRAR</Button>
                        </ButtonGroup>
                    </Col>
                </form>
            </Panel>
        )
    }
}

export default reduxForm({
    form: 'rechazos_form', // a unique identifier for this form
    validate,
})(RechazosForm);



// WEBPACK FOOTER //
// ./src/components/pages/rechazos/rechazos-form.tsx